import type { ReportRead, ReportReadRow } from 'definitions/report-read';
import { REPORT_TEXT_LIMITS } from 'definitions';
import { cn } from 'helpers/cn';
import { DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { NEXT_STEP_CHOICES, OUTSIDE_FACTOR_CHOICES, type RowVerdict } from '@/state/experiments/report-questions';
import type { ReportManager } from './ReportForm';
import { MultiChoice, Question, SingleChoice, TextAnswer } from './ReportChips';

export type RowVerdicts = Partial<Record<ReportReadRow['key'], RowVerdict>>;

type ReadStepProps = {
  manager: ReportManager;
  read: ReportRead | null;
  verdicts: RowVerdicts;
  onVerdict: (key: ReportReadRow['key'], verdict: RowVerdict) => void;
};

/** The lines that can be confirmed or corrected: the ones that speak to an outside factor. */
export const checkableRows = (read: ReportRead | null): ReportReadRow[] =>
  read?.rows.filter((row) => row.factor !== null) ?? [];

type VerdictButtonsProps = { verdict: RowVerdict; label: string; onChange: (verdict: RowVerdict) => void };

const VerdictButtons = ({ verdict, label, onChange }: VerdictButtonsProps) => (
  <div role="group" aria-label={label} className="flex gap-[5px]">
    <button
      type="button"
      aria-pressed={verdict === 'right'}
      onClick={() => onChange(verdict === 'right' ? null : 'right')}
      className={cn(
        'h-7 rounded-lg border px-2.5 text-micro transition-colors duration-150',
        verdict === 'right'
          ? 'border-ok-line bg-ok-bg text-ok'
          : 'border-input bg-panel text-foreground hover:bg-hover',
      )}
    >
      Right
    </button>
    <button
      type="button"
      aria-pressed={verdict === 'wrong'}
      onClick={() => onChange(verdict === 'wrong' ? null : 'wrong')}
      className={cn(
        'h-7 rounded-lg border px-2.5 text-micro transition-colors duration-150',
        verdict === 'wrong'
          ? 'border-warn-line bg-warn-bg text-warn'
          : 'border-input bg-panel text-foreground hover:bg-hover',
      )}
    >
      Not quite
    </button>
  </div>
);

type ReadRowProps = { row: ReportReadRow; verdict: RowVerdict; onChange: (verdict: RowVerdict) => void };

const ReadRow = ({ row, verdict, onChange }: ReadRowProps) => (
  <div className="border-line grid grid-cols-[1fr_auto] items-center gap-3 border-t px-[13px] py-[11px] first:border-t-0">
    <div className="flex min-w-0 flex-col gap-[3px]">
      <span className="micro-label text-dim">{row.label}</span>
      <span
        className={cn('text-body leading-[1.4]', verdict === 'wrong' ? 'text-dim line-through' : 'text-foreground')}
      >
        {row.statement}
      </span>
      <span className="text-dim font-mono text-[10px]">{row.source}</span>
    </div>
    {/* A line that speaks to no outside factor is context, not a claim to check. */}
    {row.factor && <VerdictButtons verdict={verdict} label={`Is this right: ${row.statement}`} onChange={onChange} />}
  </div>
);

/**
 * Screen 2: Pertento's read of the run, to confirm or correct line by line, then what is next
 * and the one thing the data cannot say.
 *
 * Confirming or correcting a line is not stored as such: it decides which outside factors the
 * report carries (see applyRowVerdicts), so what is kept is the answer, not the draft. When
 * there is nothing to check, because the read could not be loaded or had no data, the same
 * question is asked as plain chips instead.
 */
export const ReadStep = ({ manager, read, verdicts, onVerdict }: ReadStepProps) => {
  const { draft, setAnswer, toggleFactor } = manager;
  const rows = checkableRows(read);
  const checked = rows.filter((row) => verdicts[row.key]).length;
  const hasRows = !!read && read.rows.length > 0;

  // The lines speak to only some factors, so every other factor is still offered as a chip: a
  // promotion or a site release is something only the user knows.
  const covered = new Set(rows.map((row) => row.factor));
  const remaining = OUTSIDE_FACTOR_CHOICES.filter((choice) => !covered.has(choice.value));

  const days = read && read.days > 0 ? `${read.days} ${read.days === 1 ? 'day' : 'days'}` : 'the run';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <DialogTitle className="text-foreground text-title font-medium">
          {hasRows ? "Check Pertento's read of this test" : 'What else went on?'}
        </DialogTitle>
        <DialogDescription className="supporting">
          {rows.length > 0
            ? `Drafted from ${days} of data. Confirm or correct each line, then add what only you know.`
            : hasRows
              ? 'Here is what the data says. Add anything outside the test that may have affected it, then what happens next.'
              : 'Tell us anything outside the test that may have affected it, then what happens next.'}
        </DialogDescription>
      </div>

      {read && hasRows && (
        <div className="border-line rounded-card border">
          {read.rows.map((row) => (
            <ReadRow
              key={row.key}
              row={row}
              verdict={verdicts[row.key] ?? null}
              onChange={(v) => onVerdict(row.key, v)}
            />
          ))}
        </div>
      )}

      <Question
        title={
          rows.length > 0 ? 'Anything else outside the test?' : 'Anything outside the test that may have affected it?'
        }
      >
        <MultiChoice
          label="Outside factors"
          choices={remaining}
          values={draft.outsideFactors}
          onToggle={toggleFactor}
        />
      </Question>

      <Question title="What are you doing next?">
        <SingleChoice
          label="What are you doing next?"
          choices={NEXT_STEP_CHOICES}
          value={draft.nextStep}
          onChange={(value) => setAnswer('nextStep', value)}
          suggested={read?.suggestedNextStep}
        />
      </Question>

      <Question title="What do you think explains it?">
        <TextAnswer
          label="What do you think explains it?"
          value={draft.explanation}
          onChange={(value) => setAnswer('explanation', value)}
          limit={REPORT_TEXT_LIMITS.explanation}
          placeholder="Your best explanation for the result (optional)"
        />
      </Question>

      <Question title="What would you tell a colleague running a similar test?">
        <TextAnswer
          label="What would you tell a colleague running a similar test?"
          value={draft.learning}
          onChange={(value) => setAnswer('learning', value)}
          limit={REPORT_TEXT_LIMITS.learning}
          placeholder="The one thing the data cannot say (optional)"
        />
      </Question>

      {rows.length > 0 && (
        <span className="text-dim font-mono text-[10px]">
          {checked} of {rows.length} lines checked
        </span>
      )}
    </div>
  );
};
